import React, { useState, useEffect, useRef } from "react";
import moment from "moment";

export default function CountdownTimer({ order, track }) {
  const FULL_DASH_ARRAY = 1015;
  const timerIntervalRef = useRef(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [timeDifferenceInSeconds, setTimeDifferenceInSeconds] = useState(0);

  // Calculate initial time values
  useEffect(() => {
    const ogwugoTime = order?.updated_at;
    const extraMinutes = 40;
    const foodPreparationTime = order?.foods?.[0]?.food_preparation_time;
    const currentTime = moment();

    // Parse the given time string
    let parsedTime = moment(ogwugoTime, moment.ISO_8601);
    
    // Parse the food preparation time and convert to minutes
    const [prepHours, prepMinutes] = (foodPreparationTime || "00:00")
      ?.split(":")
      ?.map(Number);
    const totalPrepMinutes = prepHours * 60 + prepMinutes;

    // Add the total preparation minutes and extra minutes to the parsed time
    parsedTime = parsedTime.add(totalPrepMinutes + extraMinutes, "minutes");

    // Calculate the difference in milliseconds
    const difference = parsedTime.diff(currentTime);

    // If the difference is negative (past), return 0, otherwise return the difference in seconds
    const initialTimeLeft = Math.round(
      difference > 0 ? difference / 1000 : 0
    );

    setTimeDifferenceInSeconds(initialTimeLeft);
    setTimeLeft(initialTimeLeft);
  }, [order?.updated_at, order?.foods]);

  // Start and manage the timer
  useEffect(() => {
    if (timeDifferenceInSeconds === 0) return;

    let timePassed = 0;

    timerIntervalRef.current = setInterval(() => {
      timePassed += 1;
      const newTimeLeft = Math.max(0, timeDifferenceInSeconds - timePassed);
      
      setTimeLeft(newTimeLeft);

      if (newTimeLeft === 0) {
        clearInterval(timerIntervalRef.current);
      }
    }, 1000);

    // Cleanup function
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [timeDifferenceInSeconds]);

  const calculateTimeFraction = () => {
    if (timeDifferenceInSeconds === 0) return 0;
    const rawTimeFraction = timeLeft / timeDifferenceInSeconds;
    return (
      rawTimeFraction - (1 / timeDifferenceInSeconds) * (1 - rawTimeFraction)
    );
  };

  const calculateCircleDasharray = () => {
    return (calculateTimeFraction() * FULL_DASH_ARRAY).toFixed(0);
  };

  const formatTimeLeft = (time) => {
    const minutes = Math.floor(time / 60);
    let seconds = time % 60;
    if (seconds < 10) {
      seconds = `0${seconds}`;
    }
    return `${minutes}:${seconds}`;
  };

  // Check if order is actually completed/delivered
  const orderStatus = track?.courier_orders?.length > 0 
    ? track?.courier_orders?.[0]?.status 
    : order?.status;
  
  const isCompleted = orderStatus === "delivered" || orderStatus === "completed";

  return (
    <div
      className="relative bg-white dark:bg-[#121212] rounded-full shadow-md sm:rounded-full border dark:border-[#505050] pl-2  pt-1 max-w-[348px] h-[340px] mx-auto"
      data-test="timerComponent"
    >
      <svg
        width="331"
        height="332"
        viewBox="0 0 331 332"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="transform -scale-x-100 "
      >
        <circle
          cx="165.66"
          cy="165.755"
          r="161.309"
          stroke="#9A9898"
          strokeWidth="8"
          strokeDasharray="36 10"
        />
        <circle
          cx="165.66"
          cy="165.755"
          r="161.309"
          strokeWidth="9"
          stroke={isCompleted ? "#22C55E" : "#CE2600"}
          strokeDasharray="1015"
          strokeDashoffset={calculateCircleDasharray()}
          className="progress "
        />
      </svg>
      {isCompleted ? (
        <>
          <span className="absolute top-28 left-16 flex items-center justify-center font-bold text-4xl text-green-500 max-w-[200px] text-center">
            Order Completed
          </span>
        </>
      ) : (
        <>
          <span className="absolute top-28 left-24 flex items-center justify-center font-bold text-6xl text-primary">
            {formatTimeLeft(timeLeft)}
          </span>
          <span className="absolute top-48 left-24 max-w-[153px] text-2xl font-medium text-center">
            Minutes until delivered
          </span>
        </>
      )}
    </div>
  );
}
